import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Button, Tabs, Tab, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, LinearProgress, CircularProgress, Alert,
} from '@mui/material';
import {
  projectsAPI, clientsAPI, suppliersAPI,
  projectClientConditionsAPI, projectSupplierConditionsAPI,
} from '../services/api';

const STATUS_LABELS = {
  opportunity: 'Oportunidad', conceptual: 'Ing. Conceptual',
  offer_preparation: 'Preparando Oferta', offer_sent: 'Oferta Enviada',
  offer_approved: 'Oferta Aprobada', in_progress: 'En Ejecucion',
  commissioning: 'Puesta en Marcha', completed: 'Completado', cancelled: 'Cancelado',
};

const STATUS_COLORS = {
  opportunity: 'default', conceptual: 'info', offer_preparation: 'warning',
  offer_sent: 'secondary', offer_approved: 'success', in_progress: 'primary',
  commissioning: 'warning', completed: 'success', cancelled: 'error',
};

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

function formatDate(d) {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('es-ES'); } catch { return '-'; }
}

export default function Reports() {
  const [activeTab, setActiveTab] = useState(0);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [pResp, cResp, sResp] = await Promise.all([
        projectsAPI.list(),
        clientsAPI.list(),
        suppliersAPI.list(),
      ]);
      setProjects(pResp.data);
      setClients(cResp.data);
      setSuppliers(sResp.data);
      setError(null);
    } catch (err) {
      console.error('Error loading report data:', err);
      setError('Error al cargar datos para informes.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  const activeProjects = projects.filter((p) => p.status !== 'cancelled' && p.status !== 'completed');
  const pendingOffers = projects.filter((p) => ['offer_preparation', 'offer_sent'].includes(p.status));
  const executionProjects = projects.filter((p) => ['offer_approved', 'in_progress', 'commissioning'].includes(p.status));
  const completedProjects = projects.filter((p) => p.status === 'completed');

  const getClientName = (clientId) => clients.find((c) => c.id === clientId)?.name || '-';

  const statusCounts = {};
  projects.forEach((p) => { statusCounts[p.status] = (statusCounts[p.status] || 0) + 1; });

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>Informes</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="Estado General" />
          <Tab label="Ofertas Pendientes" />
          <Tab label="Proyectos en Ejecucion" />
          <Tab label="Plazos de Entrega" />
          <Tab label="Resumen Economico" />
          <Tab label="Clientes / Proveedores" />
        </Tabs>
      </Paper>

      {/* Tab 0: Estado General */}
      <TabPanel value={activeTab} index={0}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" fontWeight="bold" color="primary">{projects.length}</Typography>
              <Typography variant="body2" color="textSecondary">Total Proyectos</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" fontWeight="bold" color="info.main">{activeProjects.length}</Typography>
              <Typography variant="body2" color="textSecondary">Activos</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" fontWeight="bold" color="warning.main">{pendingOffers.length}</Typography>
              <Typography variant="body2" color="textSecondary">Ofertas Pendientes</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" fontWeight="bold" color="success.main">{completedProjects.length}</Typography>
              <Typography variant="body2" color="textSecondary">Completados</Typography>
            </Paper>
          </Grid>
        </Grid>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Distribucion por Estado</Typography>
          <Grid container spacing={1}>
            {Object.entries(statusCounts).map(([status, count]) => (
              <Grid item xs={6} sm={3} key={status}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Chip label={STATUS_LABELS[status] || status} size="small" color={STATUS_COLORS[status] || 'default'} />
                  <Typography fontWeight="bold">{count}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>

        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#c62828' }}>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Codigo</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Nombre</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Cliente</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Estado</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Fecha Peticion</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Plazo (sem)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {projects.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell><strong>{p.code}</strong></TableCell>
                  <TableCell>{p.name}</TableCell>
                  <TableCell>{getClientName(p.client_id)}</TableCell>
                  <TableCell><Chip label={STATUS_LABELS[p.status]} size="small" color={STATUS_COLORS[p.status] || 'default'} /></TableCell>
                  <TableCell>{formatDate(p.request_date)}</TableCell>
                  <TableCell>{p.delivery_weeks || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </TabPanel>

      {/* Tab 1: Ofertas Pendientes */}
      <TabPanel value={activeTab} index={1}>
        <Typography variant="h6" gutterBottom>Ofertas en Preparacion o Enviadas ({pendingOffers.length})</Typography>
        {pendingOffers.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}><Typography color="textSecondary">No hay ofertas pendientes.</Typography></Paper>
        ) : (
          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#c62828' }}>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Codigo</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Oferta</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Nombre</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Cliente</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Estado</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Fecha Oferta</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Tipo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingOffers.map((p) => (
                  <TableRow key={p.id} hover>
                    <TableCell><strong>{p.code}</strong></TableCell>
                    <TableCell>{p.offer_code || '-'}</TableCell>
                    <TableCell>{p.name}</TableCell>
                    <TableCell>{getClientName(p.client_id)}</TableCell>
                    <TableCell><Chip label={STATUS_LABELS[p.status]} size="small" color={STATUS_COLORS[p.status]} /></TableCell>
                    <TableCell>{formatDate(p.offer_date)}</TableCell>
                    <TableCell>{p.project_type}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}
      </TabPanel>

      {/* Tab 2: Proyectos en Ejecucion */}
      <TabPanel value={activeTab} index={2}>
        <Typography variant="h6" gutterBottom>Proyectos en Ejecucion ({executionProjects.length})</Typography>
        {executionProjects.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}><Typography color="textSecondary">No hay proyectos en ejecucion.</Typography></Paper>
        ) : (
          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#c62828' }}>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Codigo</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Nombre</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Cliente</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Estado</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Aprobado</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Plazo (sem)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Capacidad</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {executionProjects.map((p) => (
                  <TableRow key={p.id} hover>
                    <TableCell><strong>{p.code}</strong></TableCell>
                    <TableCell>{p.name}</TableCell>
                    <TableCell>{getClientName(p.client_id)}</TableCell>
                    <TableCell><Chip label={STATUS_LABELS[p.status]} size="small" color={STATUS_COLORS[p.status]} /></TableCell>
                    <TableCell>{formatDate(p.approval_date)}</TableCell>
                    <TableCell>{p.delivery_weeks || '-'}</TableCell>
                    <TableCell>{p.production_capacity || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}
      </TabPanel>

      {/* Tab 3: Plazos de Entrega */}
      <TabPanel value={activeTab} index={3}>
        <Typography variant="h6" gutterBottom>Plazos de Entrega de Proyectos Activos</Typography>
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#c62828' }}>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Codigo</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Nombre</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Cliente</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Peticion</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Aprobacion</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Plazo (sem)</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Estado</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activeProjects.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell><strong>{p.code}</strong></TableCell>
                  <TableCell>{p.name}</TableCell>
                  <TableCell>{getClientName(p.client_id)}</TableCell>
                  <TableCell>{formatDate(p.request_date)}</TableCell>
                  <TableCell>{formatDate(p.approval_date)}</TableCell>
                  <TableCell>{p.delivery_weeks || '-'}</TableCell>
                  <TableCell><Chip label={STATUS_LABELS[p.status]} size="small" color={STATUS_COLORS[p.status] || 'default'} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </TabPanel>

      {/* Tab 4: Resumen Economico */}
      <TabPanel value={activeTab} index={4}>
        <Typography variant="h6" gutterBottom>Resumen Economico por Proyecto</Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Datos economicos basados en condiciones de cobro/pago de cada proyecto.
        </Typography>
        <ProjectEconomicSummary projects={executionProjects} clients={clients} />
      </TabPanel>

      {/* Tab 5: Clientes/Proveedores */}
      <TabPanel value={activeTab} index={5}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>Clientes ({clients.length})</Typography>
            <Paper variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#c62828' }}>
                    <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Nombre</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>CIF</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Ciudad</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#fff' }} align="right">Proyectos</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clients.map((c) => (
                    <TableRow key={c.id} hover>
                      <TableCell><strong>{c.name}</strong></TableCell>
                      <TableCell>{c.cif || '-'}</TableCell>
                      <TableCell>{c.city || '-'}</TableCell>
                      <TableCell align="right">{projects.filter((p) => p.client_id === c.id).length}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>Proveedores ({suppliers.length})</Typography>
            <Paper variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#c62828' }}>
                    <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Nombre</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>CIF</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Ciudad</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Categoria</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {suppliers.map((s) => (
                    <TableRow key={s.id} hover>
                      <TableCell><strong>{s.name}</strong></TableCell>
                      <TableCell>{s.cif || '-'}</TableCell>
                      <TableCell>{s.city || '-'}</TableCell>
                      <TableCell>{s.category || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>
    </Box>
  );
}


function ProjectEconomicSummary({ projects, clients }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEconomic();
  }, [projects]);

  const loadEconomic = async () => {
    try {
      setLoading(true);
      const results = await Promise.all(
        projects.map(async (p) => {
          try {
            const [ccResp, scResp] = await Promise.all([
              projectClientConditionsAPI.list(p.id),
              projectSupplierConditionsAPI.list(p.id),
            ]);
            const income = ccResp.data.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
            const expense = scResp.data.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
            return { project: p, income, expense, margin: income - expense };
          } catch {
            return { project: p, income: 0, expense: 0, margin: 0 };
          }
        })
      );
      setData(results);
    } catch (err) {
      console.error('Error loading economic data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={24} /></Box>;

  const totalIncome = data.reduce((s, d) => s + d.income, 0);
  const totalExpense = data.reduce((s, d) => s + d.expense, 0);
  const totalMargin = totalIncome - totalExpense;

  const getClientName = (clientId) => clients.find((c) => c.id === clientId)?.name || '-';

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={4}>
          <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#e8f5e9' }}>
            <Typography variant="body2" color="textSecondary">Total Cobros</Typography>
            <Typography variant="h5" fontWeight="bold" color="success.main">{totalIncome.toLocaleString('es-ES')} EUR</Typography>
          </Paper>
        </Grid>
        <Grid item xs={4}>
          <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#ffebee' }}>
            <Typography variant="body2" color="textSecondary">Total Pagos</Typography>
            <Typography variant="h5" fontWeight="bold" color="error.main">{totalExpense.toLocaleString('es-ES')} EUR</Typography>
          </Paper>
        </Grid>
        <Grid item xs={4}>
          <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#c62828', color: '#fff' }}>
            <Typography variant="body2" sx={{ color: '#fff', opacity: 0.9 }}>Margen</Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ color: totalMargin >= 0 ? '#a5d6a7' : '#ffab91' }}>
              {totalMargin.toLocaleString('es-ES')} EUR
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#c62828' }}>
              <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Codigo</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Nombre</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Cliente</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#fff' }} align="right">Cobros (EUR)</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#fff' }} align="right">Pagos (EUR)</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#fff' }} align="right">Margen (EUR)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((d) => (
              <TableRow key={d.project.id} hover>
                <TableCell><strong>{d.project.code}</strong></TableCell>
                <TableCell>{d.project.name}</TableCell>
                <TableCell>{getClientName(d.project.client_id)}</TableCell>
                <TableCell align="right" sx={{ color: 'green' }}>{d.income.toLocaleString('es-ES')}</TableCell>
                <TableCell align="right" sx={{ color: 'red' }}>{d.expense.toLocaleString('es-ES')}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', color: d.margin >= 0 ? 'green' : 'red' }}>
                  {d.margin.toLocaleString('es-ES')}
                </TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ backgroundColor: '#fafafa' }}>
              <TableCell colSpan={3} sx={{ fontWeight: 'bold' }}>TOTAL</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', color: 'green' }}>{totalIncome.toLocaleString('es-ES')}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', color: 'red' }}>{totalExpense.toLocaleString('es-ES')}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', color: totalMargin >= 0 ? 'green' : 'red' }}>{totalMargin.toLocaleString('es-ES')}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
